import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Upload, X, Save, Image as ImageIcon, Link as LinkIcon, Phone } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

function resolveUrl(url) {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${BASE}${url}`;
}

function ImageUploadField({ label, value, onUpload, testId }) {
  const ref = useRef();
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${API}/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      onUpload(data.url);
    } catch (err) {
      console.error('Upload failed', err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid={testId}>
      <Label className="text-[11px] font-medium uppercase tracking-[0.1em] text-[#3A3632] mb-1.5 block">{label}</Label>
      <div className="flex items-center gap-3">
        {value && (
          <div className="w-16 h-16 overflow-hidden bg-[#E8E1D8] flex-shrink-0">
            <img src={resolveUrl(value)} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <button
          type="button"
          onClick={() => ref.current?.click()}
          disabled={uploading}
          className="flex items-center gap-2 px-4 py-2 text-[11px] tracking-wide border border-[#DDD7CE] text-[#3A3632] hover:border-[#3A3632] transition-colors disabled:opacity-50"
        >
          <Upload size={12} /> {uploading ? 'Uploading...' : value ? 'Change' : 'Upload'}
        </button>
        {value && (
          <button type="button" onClick={() => onUpload('')} className="p-1 text-[#7A7369] hover:text-red-500">
            <X size={14} />
          </button>
        )}
      </div>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={handleFile} />
    </div>
  );
}

function MultiImageUpload({ label, images, onUpdate, testId }) {
  const ref = useRef();
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const fd = new FormData();
    files.forEach(f => fd.append('files', f));
    try {
      const { data } = await axios.post(`${API}/upload/multiple`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      onUpdate([...images, ...data.urls]);
    } catch (err) {
      console.error('Upload failed', err);
    } finally {
      setUploading(false);
    }
  };

  const remove = (idx) => {
    onUpdate(images.filter((_, i) => i !== idx));
  };

  return (
    <div data-testid={testId}>
      <Label className="text-[11px] font-medium uppercase tracking-[0.1em] text-[#3A3632] mb-2 block">{label}</Label>
      <div className="grid grid-cols-4 gap-2 mb-3">
        {images.map((url, i) => (
          <div key={i} className="relative aspect-square bg-[#E8E1D8] overflow-hidden group">
            <img src={resolveUrl(url)} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => remove(i)}
              className="absolute top-1 right-1 w-5 h-5 bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X size={10} />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => ref.current?.click()}
        disabled={uploading}
        className="flex items-center gap-2 px-4 py-2 text-[11px] tracking-wide border border-[#DDD7CE] text-[#3A3632] hover:border-[#3A3632] transition-colors disabled:opacity-50"
      >
        <Upload size={12} /> {uploading ? 'Uploading...' : 'Add Images'}
      </button>
      <input ref={ref} type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} />
    </div>
  );
}

export default function AdminSettingsPage() {
  const { user } = useAuth();
  const { refresh } = useSettings();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings`).then(r => setForm(r.data)).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/settings`,
        form,
        { withCredentials: true }
      );
      refresh();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Save failed', err);
    } finally {
      setSaving(false);
    }
  };

  if (!form) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F6F3EE]">
      <div className="w-6 h-6 border border-[#C6A978] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <main data-testid="admin-settings-page" className="pt-20 min-h-screen bg-[#F6F3EE]">
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-[#C6A978] mb-1">Admin Panel</p>
            <h1 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl font-light tracking-tighter text-[#1E1C1A]">
              Global Settings
            </h1>
          </div>
          <button
            data-testid="save-settings-btn"
            onClick={handleSave}
            disabled={saving}
            className="bg-[#1E1C1A] text-[#F6F3EE] px-5 py-2.5 text-[11px] tracking-[0.15em] uppercase hover:bg-[#3A3632] transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            <Save size={13} /> {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Changes'}
          </button>
        </div>

        <div className="space-y-8">
          {/* WhatsApp */}
          <section data-testid="whatsapp-settings" className="bg-[#E8E1D8]/50 border border-[#DDD7CE] p-5 md:p-6">
            <div className="flex items-center gap-2 mb-4">
              <Phone size={16} className="text-[#C6A978]" />
              <h2 className="text-xs font-medium uppercase tracking-[0.15em] text-[#1E1C1A]">WhatsApp Configuration</h2>
            </div>
            <div className="max-w-sm">
              <Label className="text-[11px] font-medium uppercase tracking-[0.1em] text-[#3A3632] mb-1.5 block">WhatsApp Number</Label>
              <Input
                data-testid="whatsapp-number-input"
                value={form.whatsapp_number || ''}
                onChange={e => setForm({ ...form, whatsapp_number: e.target.value })}
                placeholder="+91XXXXXXXXXX"
                className="bg-[#F6F3EE] border-[#DDD7CE] text-[#1E1C1A] font-light text-sm"
              />
              <p className="text-[10px] text-[#7A7369] mt-1">Applied globally: Product buttons, Hero CTA, Floating button</p>
            </div>
          </section>

          {/* Social Links */}
          <section data-testid="social-settings" className="bg-[#E8E1D8]/50 border border-[#DDD7CE] p-5 md:p-6">
            <div className="flex items-center gap-2 mb-4">
              <LinkIcon size={16} className="text-[#C6A978]" />
              <h2 className="text-xs font-medium uppercase tracking-[0.15em] text-[#1E1C1A]">Social Media Links</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {['instagram', 'facebook', 'pinterest', 'youtube'].map(platform => (
                <div key={platform}>
                  <Label className="text-[11px] font-medium uppercase tracking-[0.1em] text-[#3A3632] mb-1.5 block capitalize">{platform}</Label>
                  <Input
                    data-testid={`social-${platform}-input`}
                    value={form.social_links?.[platform] || ''}
                    onChange={e => setForm({ ...form, social_links: { ...form.social_links, [platform]: e.target.value } })}
                    placeholder={`https://${platform}.com/kiraq`}
                    className="bg-[#F6F3EE] border-[#DDD7CE] text-[#1E1C1A] font-light text-sm"
                  />
                </div>
              ))}
            </div>
          </section>

          {/* Hero Banners */}
          <section data-testid="banner-settings" className="bg-[#E8E1D8]/50 border border-[#DDD7CE] p-5 md:p-6">
            <div className="flex items-center gap-2 mb-4">
              <ImageIcon size={16} className="text-[#C6A978]" />
              <h2 className="text-xs font-medium uppercase tracking-[0.15em] text-[#1E1C1A]">Hero Banner</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <ImageUploadField
                label="Desktop Banner"
                value={form.hero_banner_desktop}
                onUpload={url => setForm({ ...form, hero_banner_desktop: url })}
                testId="hero-desktop-upload"
              />
              <ImageUploadField
                label="Mobile Banner"
                value={form.hero_banner_mobile}
                onUpload={url => setForm({ ...form, hero_banner_mobile: url })}
                testId="hero-mobile-upload"
              />
            </div>
          </section>

          {/* Collection Images */}
          <section data-testid="collection-image-settings" className="bg-[#E8E1D8]/50 border border-[#DDD7CE] p-5 md:p-6">
            <div className="flex items-center gap-2 mb-4">
              <ImageIcon size={16} className="text-[#C6A978]" />
              <h2 className="text-xs font-medium uppercase tracking-[0.15em] text-[#1E1C1A]">Collection Banners</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {['pearl-edit', 'gold-edit', 'after-hours'].map(slug => (
                <ImageUploadField
                  key={slug}
                  label={slug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  value={form.collection_images?.[slug] || ''}
                  onUpload={url => setForm({ ...form, collection_images: { ...form.collection_images, [slug]: url } })}
                  testId={`collection-img-${slug}`}
                />
              ))}
            </div>
          </section>

          {/* Instagram Feed */}
          <section data-testid="instagram-image-settings" className="bg-[#E8E1D8]/50 border border-[#DDD7CE] p-5 md:p-6">
            <div className="flex items-center gap-2 mb-4">
              <ImageIcon size={16} className="text-[#C6A978]" />
              <h2 className="text-xs font-medium uppercase tracking-[0.15em] text-[#1E1C1A]">Instagram Feed Images</h2>
            </div>
            <MultiImageUpload
              label="Upload 6-8 images for the Instagram grid"
              images={form.instagram_images || []}
              onUpdate={imgs => setForm({ ...form, instagram_images: imgs })}
              testId="instagram-images-upload"
            />
          </section>
        </div>
      </div>
    </main>
  );
}
